import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EVAL_CODES, type EvalCode } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { Button, Card, Modal, Spinner } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import { useT } from '../../i18n';
import { api, type EvalEstimate, type PromptSteps } from '../../lib/api';
import { errorText } from '../../lib/errors';
import { formatCents } from '../../lib/format';

const selectClass =
  'w-full h-11 px-3 rounded-lg text-sm bg-surface-2 border-0 focus:outline-none focus:ring-2 focus:ring-ink/70';

const isEvalCode = (v: string | null): v is EvalCode => EVAL_CODES.some((c) => c === v);
const idParam = (v: string | null) => (v && /^\d+$/.test(v) ? Number(v) : null);

/** A new blind comparison of two versions of one step on the check set. */
export function AdminEvalNewPage() {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast, show } = useToast();
  const [params] = useSearchParams();
  const [code, setCode] = useState<EvalCode>(() => {
    const c = params.get('code');
    return isEvalCode(c) ? c : 'ideas';
  });
  const [picked, setPicked] = useState<{ a: number | null; b: number | null }>(() => ({
    a: idParam(params.get('a')),
    b: idParam(params.get('b')),
  }));
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);

  const prompts = useQuery<PromptSteps>({ queryKey: ['admin', 'prompts'], queryFn: () => api.adminListPrompts() });
  const estimate = useQuery<EvalEstimate>({
    queryKey: ['admin', 'eval', 'estimate', code],
    queryFn: () => api.adminEvalEstimate(code),
  });

  const step = prompts.data?.steps.find((s) => s.code === code);
  const versions = [...(step?.versions ?? [])].sort((x, y) => y.version - x.version);
  const has = (id: number | null) => id !== null && versions.some((v) => v.id === id);
  // A defaults to the working version, B to the newest other one.
  const a = has(picked.a) ? picked.a! : (step?.active?.id ?? null);
  const b = has(picked.b) && picked.b !== a ? picked.b! : (versions.find((v) => v.id !== a)?.id ?? null);
  const ready = a !== null && b !== null && a !== b && (estimate.data?.items ?? 0) > 0;

  const start = async () => {
    if (a === null || b === null) return;
    setConfirming(false);
    setStarting(true);
    try {
      const run = await api.adminCreateEvalRun({ code, aPromptId: a, bPromptId: b });
      void qc.invalidateQueries({ queryKey: ['admin', 'eval', 'runs'] });
      navigate(`/admin/eval/runs/${run.id}`, { replace: true });
    } catch (err) {
      show(errorText(err, t), 'destructive');
      setStarting(false);
    }
  };

  const versionSelect = (id: string, label: string, value: number | null, onChange: (v: number) => void) => (
    <>
      <label className="block text-sm font-medium mb-1" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className={`${selectClass} mb-3`}
        value={value ?? ''}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {versions.map((v) => (
          <option key={v.id} value={v.id}>
            {t.adminEval.versionOption(v.version, v.model, v.isActive)}
          </option>
        ))}
      </select>
    </>
  );

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.adminEval.newRun} to="/admin/eval" />

      {prompts.isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {prompts.error && <p className="text-sm text-hint">{errorText(prompts.error, t)}</p>}

      {prompts.data && (
        <Card variant="surface" padding="md">
          <label className="block text-sm font-medium mb-1" htmlFor="eval-step">
            {t.adminEval.step}
          </label>
          <select
            id="eval-step"
            className={`${selectClass} mb-3`}
            value={code}
            onChange={(e) => {
              setCode(e.target.value as EvalCode);
              setPicked({ a: null, b: null });
            }}
          >
            {EVAL_CODES.map((c) => (
              <option key={c} value={c}>
                {t.adminPrompts.steps[c]}
              </option>
            ))}
          </select>

          {versionSelect('eval-a', t.adminEval.versionA, a, (v) => setPicked({ a: v, b }))}
          {versionSelect('eval-b', t.adminEval.versionB, b, (v) => setPicked({ a, b: v }))}

          <p className="text-sm text-hint mb-4">
            {estimate.data ? t.adminEval.estimate(formatCents(estimate.data.usd), estimate.data.items) : ''}
            {estimate.error ? errorText(estimate.error, t) : ''}
          </p>

          <Button variant="ink" fullWidth disabled={!ready} loading={starting} onClick={() => setConfirming(true)}>
            {t.adminEval.run}
          </Button>
        </Card>
      )}

      <Modal open={confirming} onClose={() => setConfirming(false)}>
        <p className="text-sm mb-4">{t.adminEval.runConfirm}</p>
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={() => setConfirming(false)}>
            {t.adminEval.cancel}
          </Button>
          <Button variant="ink" fullWidth onClick={start}>
            {t.adminEval.run}
          </Button>
        </div>
      </Modal>

      {toast}
    </div>
  );
}
